import { useMemo, useState } from "react";
import { format, addMonths, startOfMonth } from "date-fns";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { ExpensePayableItem, Invoice } from "@blinkbooks/types";
import { formatCurrencyCompact, formatTooltipValue } from "./utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface Props {
  historicalBE: ExpensePayableItem[];
  historicalInv: Invoice[];
  unpaidBE: ExpensePayableItem[];
  unpaidInv: Invoice[];
  cashOnHand: number;
  loading?: boolean;
}

interface ChartPoint {
  month: string;
  sortKey: string;
  balance: number | null;
  forecast: number | null;
}

const monthKey = (dateStr: string): string | null => {
  try {
    return format(startOfMonth(new Date(dateStr)), "yyyy-MM");
  } catch {
    return null;
  }
};

const BalanceForecastChart = ({
  historicalBE,
  historicalInv,
  unpaidBE,
  unpaidInv,
  cashOnHand,
  loading = false,
}: Props) => {
  const isMobile = useIsMobile();
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const chartData = useMemo((): ChartPoint[] => {
    // --- Historical: aggregate paid items by month ---
    const histMap = new Map<string, { inflows: number; outflows: number }>();

    historicalInv
      .filter((inv) => inv.status === "paid")
      .forEach((inv) => {
        const key = monthKey(inv.dueDate || inv.issueDate);
        if (!key) return;
        const e = histMap.get(key) ?? { inflows: 0, outflows: 0 };
        e.inflows += inv.totalAmount;
        histMap.set(key, e);
      });

    historicalBE
      .filter((item) => item.status === "paid")
      .forEach((item) => {
        const date = item.payment_date || item.txn_date;
        const key = monthKey(date);
        if (!key) return;
        const e = histMap.get(key) ?? { inflows: 0, outflows: 0 };
        e.outflows += Math.abs(item.amount);
        histMap.set(key, e);
      });

    const sortedHist = Array.from(histMap.entries()).sort((a, b) =>
      a[0].localeCompare(b[0])
    );

    // Reverse-engineer opening balance so the series ends at cashOnHand
    const totalNet = sortedHist.reduce(
      (s, [, { inflows, outflows }]) => s + inflows - outflows,
      0
    );
    let cum = cashOnHand - totalNet;
    const todayKey = format(startOfMonth(new Date()), "yyyy-MM");

    const historicalPoints: ChartPoint[] = sortedHist
      .filter(([key]) => key < todayKey)
      .map(([key, { inflows, outflows }]) => {
        cum += inflows - outflows;
        return {
          month: format(new Date(key + "-01"), "MMM yyyy"),
          sortKey: key,
          balance: cum,
          forecast: null,
        };
      });

    // Bridge: last historical point also anchors the forecast line
    const lastHist = historicalPoints[historicalPoints.length - 1];
    if (lastHist) lastHist.forecast = lastHist.balance;

    // --- Forecast: group future unpaid items by due month ---
    const futureIn = new Map<string, number>();
    const futureOut = new Map<string, number>();
    const now = new Date();

    unpaidInv.forEach((inv) => {
      if (!inv.dueDate) return;
      const d = new Date(inv.dueDate);
      if (d < now) return;
      const key = format(startOfMonth(d), "yyyy-MM");
      futureIn.set(key, (futureIn.get(key) ?? 0) + inv.balance);
    });

    unpaidBE.forEach((item) => {
      if (!item.due_date) return;
      const d = new Date(item.due_date);
      if (d < now) return;
      const key = format(startOfMonth(d), "yyyy-MM");
      futureOut.set(key, (futureOut.get(key) ?? 0) + Math.abs(item.amount));
    });

    // Forecast = Current Balance + Expected Inflows − Expected Outflows (cumulative by month)
    let fBal = cashOnHand;
    const today = startOfMonth(now);
    const forecastPoints: ChartPoint[] = [];

    for (let i = 0; i <= 6; i++) {
      const monthDate = addMonths(today, i);
      const key = format(monthDate, "yyyy-MM");
      if (i > 0) {
        fBal += (futureIn.get(key) ?? 0) - (futureOut.get(key) ?? 0);
      }
      forecastPoints.push({
        month: format(monthDate, "MMM yyyy"),
        sortKey: key,
        balance: i === 0 ? cashOnHand : null,
        forecast: fBal,
      });
    }

    return [...historicalPoints, ...forecastPoints];
  }, [historicalBE, historicalInv, unpaidBE, unpaidInv, cashOnHand]);

  // Build per-month forecast breakdown for the data table
  const forecastBreakdown = useMemo(() => {
    const now = new Date();
    const months: Array<{
      key: string;
      label: string;
      inflows: number;
      outflowAmount: number;
      invoices: Array<{ customer: string; amount: number; dueDate: string }>;
      bills: Array<{ vendor: string; amount: number; dueDate: string }>;
    }> = [];

    for (let i = 1; i <= 6; i++) {
      const monthDate = addMonths(startOfMonth(now), i);
      const key = format(monthDate, "yyyy-MM");
      const label = format(monthDate, "MMM yyyy");

      const invoices = unpaidInv
        .filter(inv => inv.dueDate && new Date(inv.dueDate) >= now && format(startOfMonth(new Date(inv.dueDate)), "yyyy-MM") === key)
        .map(inv => ({ customer: inv.customerName ?? "Unknown", amount: inv.balance, dueDate: inv.dueDate! }))
        .sort((a, b) => b.amount - a.amount);

      const bills = unpaidBE
        .filter(item => item.due_date && new Date(item.due_date) >= now && format(startOfMonth(new Date(item.due_date)), "yyyy-MM") === key)
        .map(item => ({ vendor: item.vendor_name ?? item.description ?? "Unknown", amount: Math.abs(item.amount), dueDate: item.due_date! }))
        .sort((a, b) => b.amount - a.amount);

      months.push({
        key,
        label,
        inflows: invoices.reduce((s, i) => s + i.amount, 0),
        outflowAmount: bills.reduce((s, b) => s + b.amount, 0),
        invoices,
        bills,
      });
    }
    return months;
  }, [unpaidInv, unpaidBE]);

  const todayLabel = format(startOfMonth(new Date()), "MMM yyyy");

  type TooltipPayload = { payload: ChartPoint };
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: TooltipPayload[];
  }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const val = d.balance ?? d.forecast ?? 0;
    const isForecast = d.balance === null;
    return (
      <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-xl p-3 text-sm min-w-[140px]">
        <p className="font-semibold text-gray-900 mb-1">{d.month}</p>
        <p className={`font-semibold ${val < 0 ? "text-rose-600" : "text-blue-600"}`}>
          {formatTooltipValue(val)}
        </p>
        {isForecast && (
          <p className="text-[11px] text-gray-400 mt-1 italic">Projected</p>
        )}
      </div>
    );
  };

  return (
    <Card className="shadow-sm border-border/60 mt-6">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base font-semibold">Balance &amp; Forecast</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Current balance projected with open invoices and outstanding bills ·{" "}
              <span className="italic">Not affected by date filter</span>
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-muted-foreground">Loading balance data…</span>
            </div>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
              <ComposedChart
                data={chartData}
                margin={{ top: 10, right: 24, left: 20, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="bal-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e5e7eb"
                  vertical={false}
                  opacity={0.5}
                />
                <XAxis
                  dataKey="month"
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={formatCurrencyCompact}
                  width={64}
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: "#d1d5db", strokeWidth: 1 }}
                />
                <ReferenceLine
                  y={0}
                  stroke="#94a3b8"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                <ReferenceLine
                  x={todayLabel}
                  stroke="#9ca3af"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  label={{
                    value: "Today",
                    position: "insideTopRight",
                    fill: "#9ca3af",
                    fontSize: 10,
                  }}
                />
                {/* Historical balance area */}
                <Area
                  type="monotone"
                  dataKey="balance"
                  stroke="#3b82f6"
                  strokeWidth={2.5}
                  fill="url(#bal-fill)"
                  dot={false}
                  activeDot={{ r: 4, fill: "#3b82f6", strokeWidth: 0 }}
                  connectNulls={false}
                />
                {/* Forecast line (dashed) */}
                <Line
                  type="monotone"
                  dataKey="forecast"
                  stroke="#3b82f6"
                  strokeWidth={2.5}
                  strokeDasharray="7 4"
                  dot={{ r: 3, fill: "#fff", stroke: "#3b82f6", strokeWidth: 2 }}
                  activeDot={{ r: 5, stroke: "#3b82f6", strokeWidth: 2, fill: "#fff" }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>

            <div className="flex items-center gap-5 mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-5 h-1 bg-blue-500 rounded-full" />
                <span className="text-xs text-gray-600">Actual balance</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-5 h-0.5 rounded-full"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(90deg,#3b82f6 0,#3b82f6 5px,transparent 5px,transparent 9px)",
                  }}
                />
                <span className="text-xs text-gray-600">
                  Forecast (balance + expected inflows − outflows)
                </span>
              </div>
            </div>

            {/* Forecast data breakdown */}
            <div className="mt-4 border rounded-lg overflow-hidden">
              <button
                onClick={() => setBreakdownOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
              >
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Forecast sources · {unpaidInv.length} open invoice{unpaidInv.length !== 1 ? "s" : ""}, {unpaidBE.length} unpaid bill{unpaidBE.length !== 1 ? "s" : ""}
                </span>
                {breakdownOpen ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>

              {breakdownOpen && (
                <div className="divide-y">
                  {forecastBreakdown.map(month => (
                    <div key={month.key} className="px-4 py-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold">{month.label}</span>
                        <div className="flex items-center gap-3 text-xs">
                          {month.inflows > 0 && (
                            <span className="text-emerald-600 font-medium">+{formatTooltipValue(month.inflows)}</span>
                          )}
                          {month.outflowAmount > 0 && (
                            <span className="text-rose-600 font-medium">−{formatTooltipValue(month.outflowAmount)}</span>
                          )}
                          {month.inflows === 0 && month.outflowAmount === 0 && (
                            <span className="text-muted-foreground">No items due</span>
                          )}
                        </div>
                      </div>
                      {(month.invoices.length > 0 || month.bills.length > 0) && (
                        <div className="space-y-1">
                          {month.invoices.slice(0, 5).map((inv, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] text-muted-foreground">
                              <span className="truncate max-w-[200px]">↑ {inv.customer}</span>
                              <span className="tabular-nums text-emerald-600 shrink-0 ml-2">{formatTooltipValue(inv.amount)}</span>
                            </div>
                          ))}
                          {month.invoices.length > 5 && (
                            <p className="text-[10px] text-muted-foreground">+{month.invoices.length - 5} more invoices</p>
                          )}
                          {month.bills.slice(0, 5).map((bill, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] text-muted-foreground">
                              <span className="truncate max-w-[200px]">↓ {bill.vendor}</span>
                              <span className="tabular-nums text-rose-600 shrink-0 ml-2">{formatTooltipValue(bill.amount)}</span>
                            </div>
                          ))}
                          {month.bills.length > 5 && (
                            <p className="text-[10px] text-muted-foreground">+{month.bills.length - 5} more bills</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BalanceForecastChart;
